// src/features/product-tours/definitions/allTours.ts
// Guides interactifs — un par module menu ; textes via i18n (tours.*).

import type { DriveStep } from 'driver.js';
import type { BuildTourStepsContext, GuideEligibility, ProductTourDefinition, ProductTourId } from '../types';
import { navThen } from '../mobileTourNav';
import {
  anchorsToSteps,
  buildCreateOpenStep,
  buildNavHighlightSteps,
  compactSteps,
  step,
  type TourAnchor,
} from '../tourStepHelpers';
import { canSellFromAnySite, isFeatureEnabled } from '../../../types/business';
import {
  helpTourDefinition,
  helpGuidesTourDefinition,
  helpFaqTourDefinition,
  helpTicketsTourDefinition,
} from './helpTours';

export {
  helpTourDefinition,
  helpGuidesTourDefinition,
  helpFaqTourDefinition,
  helpTicketsTourDefinition,
};

type AnchorSpec = {
  selector: string;
  /** Clé sous tours.<i18n>. — ex. filterPeriodTitle */
  titleKey: string;
  descKey: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  requirePresent?: boolean;
};

type ModuleTourSpec = {
  id: ProductTourId;
  ensurePath: string;
  eligibility?: GuideEligibility;
  /** Sous-menu parent (ex. Stock) — étape 1 si renseigné et ≠ leaf. */
  navParentSelector?: string;
  navSelector: string;
  titleSelector: string;
  /** Préfixe i18n sous tours.<prefix>.* */
  i18n: string;
  actionsSelector?: string;
  contentSelector?: string;
  /** Bloc filtres (fallback si filterAnchors vide). */
  filtersSelector?: string;
  /** Un filtre = une étape (prioritaire sur filtersSelector). */
  filterAnchors?: AnchorSpec[];
  /** Stats / KPI optionnels (ex. rapports caisse). */
  statsAnchors?: AnchorSpec[];
  /** Bouton ouvrir création (ouvre le drawer au Suivant). */
  createOpenSelector?: string;
  /** Champs du formulaire de création (après ouverture). */
  createFieldAnchors?: AnchorSpec[];
  /** @deprecated préfère createOpenSelector */
  createSelector?: string;
  navSide?: 'top' | 'bottom' | 'left' | 'right';
};

function resolveAnchors(
  t: BuildTourStepsContext['t'],
  i18nPrefix: string,
  specs: AnchorSpec[] | undefined
): TourAnchor[] {
  if (!specs?.length) return [];
  const p = `tours.${i18nPrefix}`;
  return specs.map((s) => ({
    selector: s.selector,
    title: t(`${p}.${s.titleKey}`),
    description: t(`${p}.${s.descKey}`),
    side: s.side,
    requirePresent: s.requirePresent,
  }));
}

function buildModuleTour(spec: ModuleTourSpec): ProductTourDefinition {
  const {
    id,
    ensurePath,
    eligibility,
    navParentSelector,
    navSelector,
    titleSelector,
    i18n,
    actionsSelector,
    contentSelector,
    filtersSelector,
    filterAnchors,
    statsAnchors,
    createOpenSelector,
    createFieldAnchors,
    createSelector,
    navSide = 'right',
  } = spec;
  const p = `tours.${i18n}`;
  const openCreate = createOpenSelector || createSelector;

  return {
    id,
    ensurePath,
    eligibility,
    requiredSelectors: [titleSelector],
    buildSteps: ({ t, navigate, pathname, layoutShell }: BuildTourStepsContext) => {
      const navSteps = buildNavHighlightSteps({
        parentSelector: navParentSelector,
        parentTitle: t(`${p}.stepNavParentTitle`, { defaultValue: t(`${p}.stepNavTitle`) }),
        parentDesc: t(`${p}.stepNavParentDesc`, { defaultValue: t(`${p}.stepNavDesc`) }),
        leafSelector: navSelector,
        leafTitle: t(`${p}.stepNavTitle`),
        leafDesc: t(`${p}.stepNavDesc`),
        ensurePath,
        pathname,
        navigate,
        layoutShell,
        side: navSide,
      });

      // Après navigation depuis Aide : ne pas tester le DOM au build (sinon création/filtres sautés).
      const filterSteps = filterAnchors?.length
        ? anchorsToSteps(
            resolveAnchors(t, i18n, filterAnchors).map((a) => ({
              ...a,
              requirePresent: false,
            }))
          )
        : filtersSelector
          ? [
              step(
                filtersSelector,
                t(`${p}.stepFiltersTitle`, { defaultValue: t(`${p}.stepActions`) }),
                t(`${p}.stepFiltersDesc`, { defaultValue: t(`${p}.stepActionsDesc`) })
              ),
            ]
          : [];

      const statsSteps = anchorsToSteps(
        resolveAnchors(t, i18n, statsAnchors).map((a) => ({
          ...a,
          requirePresent: false,
        }))
      );

      const createOpen = openCreate
        ? buildCreateOpenStep(
            openCreate,
            t(`${p}.stepCreateTitle`, { defaultValue: t(`${p}.stepActions`) }),
            t(`${p}.stepCreateDesc`, { defaultValue: t(`${p}.stepActionsDesc`) })
          )
        : null;

      const createFields = createFieldAnchors?.length
        ? anchorsToSteps(
            resolveAnchors(t, i18n, createFieldAnchors).map((a) => ({
              ...a,
              requirePresent: false,
            }))
          )
        : [];

      return compactSteps([
        ...navSteps,
        step(titleSelector, t(`${p}.stepTitle`), t(`${p}.stepTitleDesc`)),
        contentSelector
          ? step(contentSelector, t(`${p}.stepContent`), t(`${p}.stepContentDesc`), 'top')
          : null,
        ...filterSteps,
        ...statsSteps,
        actionsSelector
          ? step(actionsSelector, t(`${p}.stepActions`), t(`${p}.stepActionsDesc`))
          : null,
        createOpen,
        ...(openCreate ? createFields : []),
      ]);
    },
  };
}

export const refreshAppTourDefinition: ProductTourDefinition = {
  id: 'refresh_app_v1',
  requiredSelectors: ['[data-tour="refresh-app"]'],
  buildSteps: ({ t }) => [
    {
      element: '[data-tour="refresh-app"]',
      popover: {
        title: t('tours.refresh.stepBtnTitle'),
        description: t('tours.refresh.stepBtnDesc'),
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: '[data-tour="help-nav"]',
      popover: {
        title: t('tours.refresh.stepHelpTitle'),
        description: t('tours.refresh.stepHelpDesc'),
        side: 'bottom',
        align: 'end',
      },
    },
  ],
};

export const pushNotificationsTourDefinition: ProductTourDefinition = {
  id: 'push_notifications_v1',
  eligibility: { requiresMobile: true },
  requiredSelectors: ['[data-tour="account-menu"]'],
  buildSteps: ({ t }) => [
    {
      element: '[data-tour="help-nav"]',
      popover: {
        title: t('tours.push.stepIntroTitle'),
        description: t('tours.push.stepIntroDesc'),
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: '[data-tour="account-menu"]',
      popover: {
        title: t('tours.push.stepSettingsTitle'),
        description: t('tours.push.stepSettingsDesc'),
        side: 'bottom',
        align: 'end',
      },
    },
  ],
};

export const posSaleTourDefinition: ProductTourDefinition = {
  id: 'pos_sale_v1',
  ensurePath: '/pos/transaction',
  eligibility: {
    requiresFeatures: ['pos'],
    requiresAnyPermissions: ['canViewPrestations', 'canViewProductSales'],
  },
  requiredSelectors: ['[data-tour="pos-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell, business }) => {
    const showSite = canSellFromAnySite(business ?? null);
    return compactSteps([
      {
        element: '[data-tour="nav-pos"]',
        popover: {
          title: t('tours.pos.stepNavTitle'),
          description: t('tours.pos.stepNavDesc'),
          side: 'right',
          align: 'start',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen('/pos/transaction', pathname, navigate, drv, layoutShell),
        },
      },
      step('[data-tour="pos-title"]', t('tours.pos.stepTitle'), t('tours.pos.stepTitleDesc')),
      showSite
        ? step(
            '[data-tour="pos-site"]',
            t('tours.pos.stepSiteTitle'),
            t('tours.pos.stepSiteDesc')
          )
        : null,
      step('[data-tour="pos-client"]', t('tours.pos.stepClient'), t('tours.pos.stepClientDesc')),
      step('[data-tour="pos-items"]', t('tours.pos.stepItems'), t('tours.pos.stepItemsDesc')),
      step('[data-tour="pos-cart"]', t('tours.pos.stepCart'), t('tours.pos.stepCartDesc'), 'left'),
      step(
        '[data-tour="pos-payment-splits"]',
        t('tours.pos.stepPaymentSplits'),
        t('tours.pos.stepPaymentSplitsDesc'),
        'left'
      ),
    ]);
  },
};

/** Vente avec paiement en plusieurs (depuis la caisse). */
export const posInstallmentTourDefinition: ProductTourDefinition = {
  id: 'pos_installment_v1',
  ensurePath: '/pos/transaction',
  eligibility: {
    requiresFeatures: ['pos', 'paymentPlans'],
    requiresPermissions: ['canCreatePaymentPlans'],
  },
  requiredSelectors: ['[data-tour="pos-cart"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell, business }) => {
    const showSite = canSellFromAnySite(business ?? null);
    return compactSteps([
      {
        element: '[data-tour="nav-pos"]',
        popover: {
          title: t('tours.posInstallment.stepNavTitle'),
          description: t('tours.posInstallment.stepNavDesc'),
          side: 'right',
          align: 'start',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen('/pos/transaction', pathname, navigate, drv, layoutShell),
        },
      },
      showSite
        ? step(
            '[data-tour="pos-site"]',
            t('tours.pos.stepSiteTitle'),
            t('tours.pos.stepSiteDesc')
          )
        : null,
      step(
        '[data-tour="pos-client"]',
        t('tours.posInstallment.stepClient'),
        t('tours.posInstallment.stepClientDesc')
      ),
      step(
        '[data-tour="pos-items"]',
        t('tours.posInstallment.stepItems'),
        t('tours.posInstallment.stepItemsDesc')
      ),
      step(
        '[data-tour="pos-cart"]',
        t('tours.posInstallment.stepCart'),
        t('tours.posInstallment.stepCartDesc'),
        'left'
      ),
      {
        element: '[data-tour="pos-payment-plan"], [data-tour="pos-payment-plan-hint"]',
        popover: {
          title: t('tours.posInstallment.stepPlan'),
          description: t('tours.posInstallment.stepPlanDesc'),
          side: 'left',
          align: 'start',
        },
      },
    ]);
  },
};

/** Paie de fin de mois (génération, ajustements, notes de frais optionnelles). */
export const payrollsTourDefinition: ProductTourDefinition = {
  id: 'payrolls_v1',
  ensurePath: '/payrolls',
  eligibility: {
    requiresFeatures: ['employeesPayroll'],
    requiresPermissions: ['canViewPayrolls'],
  },
  requiredSelectors: ['[data-tour="payrolls-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell }) =>
    compactSteps([
      ...buildNavHighlightSteps({
        parentSelector: '[data-tour="nav-hr"]',
        parentTitle: t('tours.payrolls.stepNavParentTitle', {
          defaultValue: t('tours.payrolls.stepNavTitle'),
        }),
        parentDesc: t('tours.payrolls.stepNavParentDesc', {
          defaultValue: t('tours.payrolls.stepNavDesc'),
        }),
        leafSelector: '[data-tour="nav-payrolls"]',
        leafTitle: t('tours.payrolls.stepNavTitle'),
        leafDesc: t('tours.payrolls.stepNavDesc'),
        ensurePath: '/payrolls',
        pathname,
        navigate,
        layoutShell,
      }),
      step(
        '[data-tour="payrolls-title"]',
        t('tours.payrolls.stepTitle'),
        t('tours.payrolls.stepTitleDesc')
      ),
      step(
          '[data-tour="payrolls-content"]',
          t('tours.payrolls.stepPeriod'),
          t('tours.payrolls.stepPeriodDesc')
        ),
      step(
        '[data-tour="payrolls-generate"]',
        t('tours.payrolls.stepGenerate'),
        t('tours.payrolls.stepGenerateDesc'),
        'top'
      ),
      step(
        '[data-tour="payrolls-adjustments"]',
        t('tours.payrolls.stepAdjustments'),
        t('tours.payrolls.stepAdjustmentsDesc'),
        'top'
      ),
      step(
        '[data-tour="payrolls-content"]',
        t('tours.payrolls.stepTips'),
        t('tours.payrolls.stepTipsDesc'),
        'top'
      ),
    ]),
};

/** Rapports d’activité — insiste sur la config par employé. */
export const activityReportsTourDefinition: ProductTourDefinition = {
  id: 'activity_reports_v1',
  ensurePath: '/activity/reports',
  eligibility: {
    requiresFeatures: ['activityReports'],
    requiresPermissions: ['canViewActivityReports'],
  },
  requiredSelectors: ['[data-tour="activity-reports-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell }) =>
    compactSteps([
      ...buildNavHighlightSteps({
        parentSelector: '[data-tour="nav-activity"]',
        parentTitle: t('tours.activityReports.stepNavParentTitle', {
          defaultValue: t('tours.activityReports.stepNavTitle'),
        }),
        parentDesc: t('tours.activityReports.stepNavParentDesc', {
          defaultValue: t('tours.activityReports.stepNavDesc'),
        }),
        leafSelector: '[data-tour="nav-activity-reports"]',
        leafTitle: t('tours.activityReports.stepNavTitle'),
        leafDesc: t('tours.activityReports.stepNavDesc'),
        ensurePath: '/activity/reports',
        pathname,
        navigate,
        layoutShell,
      }),
      step(
        '[data-tour="activity-reports-title"]',
        t('tours.activityReports.stepTitle'),
        t('tours.activityReports.stepTitleDesc')
      ),
      step(
        '[data-tour="activity-reports-filter-period"]',
        t('tours.activityReports.filterPeriodTitle', {
          defaultValue: t('tours.activityReports.stepActions'),
        }),
        t('tours.activityReports.filterPeriodDesc', {
          defaultValue: t('tours.activityReports.stepActionsDesc'),
        })
      ),
      step(
        '[data-tour="activity-reports-filter-employee"]',
        t('tours.activityReports.filterEmployeeTitle', {
          defaultValue: t('tours.activityReports.stepActions'),
        }),
        t('tours.activityReports.filterEmployeeDesc', {
          defaultValue: t('tours.activityReports.stepActionsDesc'),
        })
      ),
      step(
        '[data-tour="activity-reports-config-tip"]',
        t('tours.activityReports.stepConfig'),
        t('tours.activityReports.stepConfigDesc')
      ),
      step(
        '[data-tour="activity-reports-actions"]',
        t('tours.activityReports.stepActions'),
        t('tours.activityReports.stepActionsDesc')
      ),
      step(
        '[data-tour="activity-reports-content"]',
        t('tours.activityReports.stepContent'),
        t('tours.activityReports.stepContentDesc'),
        'top'
      ),
    ]),
};

/** Catégories de prestations (prérequis avant création de prestation). */
export const serviceCategoriesTourDefinition: ProductTourDefinition = {
  id: 'service_categories_v1',
  ensurePath: '/services/categories',
  eligibility: {
    requiresFeatures: ['services'],
    requiresPermissions: ['canViewServices'],
  },
  requiredSelectors: ['[data-tour="service-categories-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell }) =>
    compactSteps([
      {
        element: '[data-tour="account-menu"]',
        popover: {
          title: t('tours.serviceCategories.stepNavTitle'),
          description: t('tours.serviceCategories.stepNavDesc'),
          side: 'bottom',
          align: 'end',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen('/services/categories', pathname, navigate, drv, layoutShell),
        },
      },
      step(
        '[data-tour="service-categories-title"]',
        t('tours.serviceCategories.stepTitle'),
        t('tours.serviceCategories.stepTitleDesc')
      ),
      step(
        '[data-tour="service-categories-list"]',
        t('tours.serviceCategories.stepContent'),
        t('tours.serviceCategories.stepContentDesc'),
        'top'
      ),
      step(
        '[data-tour="service-categories-filter-search"]',
        t('tours.serviceCategories.filterSearchTitle'),
        t('tours.serviceCategories.filterSearchDesc')
      ),
      buildCreateOpenStep(
        '[data-tour="service-categories-create"]',
        t('tours.serviceCategories.stepCreateTitle'),
        t('tours.serviceCategories.stepCreateDesc')
      ),
      step(
        '[data-tour="service-categories-field-name"]',
        t('tours.serviceCategories.fieldNameTitle'),
        t('tours.serviceCategories.fieldNameDesc')
      ),
      step(
        '[data-tour="service-categories-field-color"]',
        t('tours.serviceCategories.fieldColorTitle'),
        t('tours.serviceCategories.fieldColorDesc')
      ),
      step(
        '[data-tour="service-categories-field-description"]',
        t('tours.serviceCategories.fieldDescTitle'),
        t('tours.serviceCategories.fieldDescDesc')
      ),
    ]),
};

/** Rapports de caisse — génération page + export avancé. */
export const posReportsTourDefinition: ProductTourDefinition = {
  id: 'pos_reports_v1',
  ensurePath: '/pos/reports',
  eligibility: {
    requiresFeatures: ['pos'],
    requiresPermissions: ['canViewCashReports'],
  },
  requiredSelectors: ['[data-tour="pos-reports-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell, permissions }) => {
    const canAdvanced = !!permissions?.canViewAdvancedCashReports;
    return compactSteps([
      ...buildNavHighlightSteps({
        parentSelector: '[data-tour="nav-pos-histories"]',
        parentTitle: t('tours.posReports.stepNavParentTitle'),
        parentDesc: t('tours.posReports.stepNavParentDesc'),
        leafSelector: '[data-tour="nav-pos-cash-reports"]',
        leafTitle: t('tours.posReports.stepNavTitle'),
        leafDesc: t('tours.posReports.stepNavDesc'),
        ensurePath: '/pos/reports',
        pathname,
        navigate,
        layoutShell,
      }),
      step(
        '[data-tour="pos-reports-title"]',
        t('tours.posReports.stepTitle'),
        t('tours.posReports.stepTitleDesc')
      ),
      step(
        '[data-tour="pos-reports-period-control"]',
        t('tours.posReports.filterPeriodTitle'),
        t('tours.posReports.filterPeriodDesc')
      ),
      step(
        '[data-tour="pos-reports-filter-site"]',
        t('tours.posReports.filterSiteTitle'),
        t('tours.posReports.filterSiteDesc')
      ),
      step(
        '[data-tour="pos-reports-stats"]',
        t('tours.posReports.statsTitle'),
        t('tours.posReports.statsDesc'),
        'top'
      ),
      step(
        '[data-tour="pos-reports-stat-revenue"]',
        t('tours.posReports.statRevenueTitle'),
        t('tours.posReports.statRevenueDesc'),
        'top'
      ),
      step(
        '[data-tour="pos-reports-list"]',
        t('tours.posReports.stepListTitle'),
        t('tours.posReports.stepListDesc'),
        'top'
      ),
      canAdvanced
        ? buildCreateOpenStep(
            '[data-tour="pos-reports-advanced-open"]',
            t('tours.posReports.stepAdvancedOpenTitle'),
            t('tours.posReports.stepAdvancedOpenDesc')
          )
        : null,
      canAdvanced
        ? step(
            '[data-tour="pos-reports-advanced-period"]',
            t('tours.posReports.advPeriodTitle'),
            t('tours.posReports.advPeriodDesc')
          )
        : null,
      canAdvanced
        ? step(
            '[data-tour="pos-reports-advanced-employee"]',
            t('tours.posReports.advEmployeeTitle'),
            t('tours.posReports.advEmployeeDesc')
          )
        : null,
      canAdvanced
        ? step(
            '[data-tour="pos-reports-advanced-service-cats"]',
            t('tours.posReports.advServiceCatsTitle'),
            t('tours.posReports.advServiceCatsDesc')
          )
        : null,
      canAdvanced
        ? step(
            '[data-tour="pos-reports-advanced-product-cat"]',
            t('tours.posReports.advProductCatTitle'),
            t('tours.posReports.advProductCatDesc')
          )
        : null,
      canAdvanced
        ? step(
            '[data-tour="pos-reports-advanced-types"]',
            t('tours.posReports.advTypesTitle'),
            t('tours.posReports.advTypesDesc')
          )
        : null,
      canAdvanced
        ? step(
            '[data-tour="pos-reports-advanced-grouping"]',
            t('tours.posReports.advGroupingTitle'),
            t('tours.posReports.advGroupingDesc')
          )
        : null,
      canAdvanced
        ? step(
            '[data-tour="pos-reports-advanced-format"]',
            t('tours.posReports.advFormatTitle'),
            t('tours.posReports.advFormatDesc'),
            'top'
          )
        : null,
      canAdvanced
        ? step(
            '[data-tour="pos-reports-advanced-download"]',
            t('tours.posReports.advDownloadTitle'),
            t('tours.posReports.advDownloadDesc'),
            'top'
          )
        : null,
    ]);
  },
};

/** Modifier / corriger / rembourser / annuler une vente depuis les rapports. */
export const posSaleCorrectionTourDefinition: ProductTourDefinition = {
  id: 'pos_sale_correction_v1',
  ensurePath: '/pos/reports',
  eligibility: {
    requiresFeatures: ['pos'],
    requiresPermissions: ['canViewCashReports'],
    requiresAnyPermissions: ['canCorrectSales', 'canIssueSaleCredits', 'canVoidSales'],
  },
  requiredSelectors: ['[data-tour="pos-reports-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell, permissions }) => {
    const canCorrect = !!permissions?.canCorrectSales;
    const canCredit = !!permissions?.canIssueSaleCredits;
    const canVoid = !!permissions?.canVoidSales;
    return compactSteps([
      ...buildNavHighlightSteps({
        parentSelector: '[data-tour="nav-pos-histories"]',
        parentTitle: t('tours.posSaleCorrection.stepNavParentTitle'),
        parentDesc: t('tours.posSaleCorrection.stepNavParentDesc'),
        leafSelector: '[data-tour="nav-pos-cash-reports"]',
        leafTitle: t('tours.posSaleCorrection.stepNavTitle'),
        leafDesc: t('tours.posSaleCorrection.stepNavDesc'),
        ensurePath: '/pos/reports',
        pathname,
        navigate,
        layoutShell,
      }),
      step(
        '[data-tour="pos-reports-title"]',
        t('tours.posSaleCorrection.stepTitle'),
        t('tours.posSaleCorrection.stepTitleDesc')
      ),
      step(
        '[data-tour="pos-reports-list"]',
        t('tours.posSaleCorrection.stepListTitle'),
        t('tours.posSaleCorrection.stepListDesc'),
        'top'
      ),
      buildCreateOpenStep(
        '[data-tour="pos-reports-transaction-actions"]',
        t('tours.posSaleCorrection.stepActionsTitle'),
        t('tours.posSaleCorrection.stepActionsDesc')
      ),
      canCorrect
        ? step(
            '[data-tour="pos-reports-action-correct"]',
            t('tours.posSaleCorrection.stepCorrectTitle'),
            t('tours.posSaleCorrection.stepCorrectDesc'),
            'left'
          )
        : null,
      canCredit
        ? step(
            '[data-tour="pos-reports-action-credit"]',
            t('tours.posSaleCorrection.stepCreditTitle'),
            t('tours.posSaleCorrection.stepCreditDesc'),
            'left'
          )
        : null,
      canVoid
        ? step(
            '[data-tour="pos-reports-action-void"]',
            t('tours.posSaleCorrection.stepVoidTitle'),
            t('tours.posSaleCorrection.stepVoidDesc'),
            'left'
          )
        : null,
      step(
        '[data-tour="pos-reports-title"]',
        t('tours.posSaleCorrection.stepConfirmTitle'),
        t('tours.posSaleCorrection.stepConfirmDesc')
      ),
    ]);
  },
};

/** Packs catalogue — config → création → caisse → rapports. */
export const catalogPacksTourDefinition: ProductTourDefinition = {
  id: 'catalog_packs_v1',
  ensurePath: '/settings/packs',
  eligibility: {
    requiresFeatures: ['catalogPacks'],
    requiresPermissions: ['canViewCatalogPacks'],
  },
  requiredSelectors: ['[data-tour="catalog-packs-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell, permissions }) => {
    const canManage = !!permissions?.canManageCatalogPacks;
    const p = 'tours.catalogPacks';
    return compactSteps([
      {
        element: '[data-tour="account-menu"]',
        popover: {
          title: t(`${p}.stepAccountTitle`),
          description: t(`${p}.stepAccountDesc`),
          side: 'bottom',
          align: 'end',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen('/settings', pathname, navigate, drv, layoutShell),
        },
      },
      step(
        '[data-tour="settings-hub-title"]',
        t(`${p}.stepSettingsHubTitle`),
        t(`${p}.stepSettingsHubDesc`)
      ),
      {
        element: '[data-tour="settings-hub-card-catalog-packs"]',
        popover: {
          title: t(`${p}.stepSettingsPacksCardTitle`),
          description: t(`${p}.stepSettingsPacksCardDesc`),
          side: 'bottom',
          align: 'start',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen('/settings/packs', pathname, navigate, drv, layoutShell),
        },
      },
      step(
        '[data-tour="catalog-packs-title"]',
        t(`${p}.stepTitle`),
        t(`${p}.stepTitleDesc`)
      ),
      step(
        '[data-tour="catalog-packs-content"]',
        t(`${p}.stepListTitle`),
        t(`${p}.stepListDesc`),
        'top'
      ),
      canManage
        ? buildCreateOpenStep(
            '[data-tour="catalog-packs-create"]',
            t(`${p}.stepCreateTitle`),
            t(`${p}.stepCreateDesc`)
          )
        : null,
      canManage
        ? step(
            '[data-tour="catalog-packs-field-name"]',
            t(`${p}.fieldNameTitle`),
            t(`${p}.fieldNameDesc`)
          )
        : null,
      canManage
        ? step(
            '[data-tour="catalog-packs-field-price"]',
            t(`${p}.fieldPriceTitle`),
            t(`${p}.fieldPriceDesc`)
          )
        : null,
      canManage
        ? step(
            '[data-tour="catalog-packs-field-components"]',
            t(`${p}.fieldComponentsTitle`),
            t(`${p}.fieldComponentsDesc`),
            'top'
          )
        : null,
      {
        element: '[data-tour="nav-pos"]',
        popover: {
          title: t(`${p}.stepPosNavTitle`),
          description: t(`${p}.stepPosNavDesc`),
          side: 'right',
          align: 'start',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen('/pos/transaction', pathname, navigate, drv, layoutShell),
        },
      },
      step('[data-tour="pos-items"]', t(`${p}.stepPosItemsTitle`), t(`${p}.stepPosItemsDesc`)),
      step(
        '[data-tour="pos-cart"]',
        t(`${p}.stepPosCartTitle`),
        t(`${p}.stepPosCartDesc`),
        'left'
      ),
      {
        element: '[data-tour="nav-pos-cash-reports"], [data-tour="nav-pos-histories"]',
        popover: {
          title: t(`${p}.stepReportsNavTitle`),
          description: t(`${p}.stepReportsNavDesc`),
          side: 'right',
          align: 'start',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen('/pos/reports', pathname, navigate, drv, layoutShell),
        },
      },
      step(
        '[data-tour="pos-reports-title"]',
        t(`${p}.stepReportsTitle`),
        t(`${p}.stepReportsDesc`)
      ),
      step(
        '[data-tour="pos-reports-list"]',
        t(`${p}.stepReportsListTitle`),
        t(`${p}.stepReportsListDesc`),
        'top'
      ),
    ]);
  },
};

/** Ordres de fabrication — lien avec les prestations. */
export const manufacturingTourDefinition: ProductTourDefinition = {
  id: 'manufacturing_v1',
  ensurePath: '/manufacturing/orders',
  eligibility: {
    requiresFeatures: ['manufacturing'],
    requiresPermissions: ['canViewManufacturingOrders'],
  },
  requiredSelectors: ['[data-tour="manufacturing-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell }) =>
    compactSteps([
      ...buildNavHighlightSteps({
        leafSelector: '[data-tour="nav-manufacturing"]',
        parentTitle: t('tours.manufacturing.stepNavTitle'),
        parentDesc: t('tours.manufacturing.stepNavDesc'),
        leafTitle: t('tours.manufacturing.stepNavTitle'),
        leafDesc: t('tours.manufacturing.stepNavDesc'),
        ensurePath: '/manufacturing/orders',
        pathname,
        navigate,
        layoutShell,
      }),
      step(
        '[data-tour="manufacturing-title"]',
        t('tours.manufacturing.stepTitle'),
        t('tours.manufacturing.stepTitleDesc')
      ),
      step(
        '[data-tour="manufacturing-link-tip"]',
        t('tours.manufacturing.stepLink'),
        t('tours.manufacturing.stepLinkDesc'),
        'bottom'
      ),
      step(
        '[data-tour="manufacturing-filter-status"]',
        t('tours.manufacturing.filterStatusTitle', {
          defaultValue: t('tours.manufacturing.stepActions'),
        }),
        t('tours.manufacturing.filterStatusDesc', {
          defaultValue: t('tours.manufacturing.stepActionsDesc'),
        })
      ),
      step(
        '[data-tour="manufacturing-actions"]',
        t('tours.manufacturing.stepActions'),
        t('tours.manufacturing.stepActionsDesc')
      ),
      step(
        '[data-tour="manufacturing-content"]',
        t('tours.manufacturing.stepContent'),
        t('tours.manufacturing.stepContentDesc'),
        'top'
      ),
    ]),
};

const MODULE_TOURS: ModuleTourSpec[] = [
  {
    id: 'pos_histories_v1',
    ensurePath: '/pos/prestations-history',
    eligibility: {
      requiresFeatures: ['pos'],
      requiresAnyPermissions: ['canViewPrestations', 'canViewProductSales'],
    },
    navParentSelector: '[data-tour="nav-pos-histories"]',
    navSelector: '[data-tour="nav-pos-prestations-history"]',
    titleSelector: '[data-tour="pos-histories-title"]',
    contentSelector: '[data-tour="pos-histories-content"]',
    filterAnchors: [
      {
        selector: '[data-tour="pos-histories-filter-period"]',
        titleKey: 'filterPeriodTitle',
        descKey: 'filterPeriodDesc',
      },
      {
        selector: '[data-tour="pos-histories-filter-range"]',
        titleKey: 'filterRangeTitle',
        descKey: 'filterRangeDesc',
      },
      {
        selector: '[data-tour="pos-histories-filter-search"]',
        titleKey: 'filterSearchTitle',
        descKey: 'filterSearchDesc',
      },
      {
        selector: '[data-tour="pos-histories-filter-employee"]',
        titleKey: 'filterEmployeeTitle',
        descKey: 'filterEmployeeDesc',
      },
      {
        selector: '[data-tour="pos-histories-filter-site"]',
        titleKey: 'filterSiteTitle',
        descKey: 'filterSiteDesc',
      },
      {
        selector: '[data-tour="pos-histories-filter-archived"]',
        titleKey: 'filterArchivedTitle',
        descKey: 'filterArchivedDesc',
      },
    ],
    statsAnchors: [
      {
        selector: '[data-tour="pos-histories-stats"]',
        titleKey: 'statsTitle',
        descKey: 'statsDesc',
        side: 'top',
      },
    ],
    i18n: 'posHistories',
  },
  {
    id: 'payment_plans_v1',
    ensurePath: '/payment-plans',
    eligibility: {
      requiresFeatures: ['paymentPlans'],
      requiresPermissions: ['canViewPaymentPlans'],
    },
    navSelector: '[data-tour="nav-payment-plans"]',
    titleSelector: '[data-tour="payment-plans-title"]',
    actionsSelector: '[data-tour="payment-plans-actions"]',
    contentSelector: '[data-tour="payment-plans-content"]',
    filterAnchors: [
      {
        selector: '[data-tour="payment-plans-filter-status"]',
        titleKey: 'filterStatusTitle',
        descKey: 'filterStatusDesc',
      },
      {
        selector: '[data-tour="payment-plans-filter-search"]',
        titleKey: 'filterSearchTitle',
        descKey: 'filterSearchDesc',
      },
    ],
    i18n: 'paymentPlans',
  },
  {
    id: 'clients_v1',
    ensurePath: '/clients',
    eligibility: {
      requiresFeatures: ['clients'],
      requiresPermissions: ['canViewClients'],
    },
    navSelector: '[data-tour="nav-clients"]',
    titleSelector: '[data-tour="clients-title"]',
    actionsSelector: '[data-tour="clients-actions"]',
    contentSelector: '[data-tour="clients-content"]',
    filterAnchors: [
      {
        selector: '[data-tour="clients-filter-search"]',
        titleKey: 'filterSearchTitle',
        descKey: 'filterSearchDesc',
      },
      {
        selector: '[data-tour="clients-filter-birthday"]',
        titleKey: 'filterBirthdayTitle',
        descKey: 'filterBirthdayDesc',
      },
      {
        selector: '[data-tour="clients-filter-loyalty"]',
        titleKey: 'filterLoyaltyTitle',
        descKey: 'filterLoyaltyDesc',
      },
      {
        selector: '[data-tour="clients-filter-no-birthday"]',
        titleKey: 'filterNoBirthdayTitle',
        descKey: 'filterNoBirthdayDesc',
      },
      {
        selector: '[data-tour="clients-filter-no-phone"]',
        titleKey: 'filterNoPhoneTitle',
        descKey: 'filterNoPhoneDesc',
      },
      {
        selector: '[data-tour="clients-filter-never-visited"]',
        titleKey: 'filterNeverVisitedTitle',
        descKey: 'filterNeverVisitedDesc',
      },
      {
        selector: '[data-tour="clients-filter-inactive"]',
        titleKey: 'filterInactiveTitle',
        descKey: 'filterInactiveDesc',
      },
    ],
    createOpenSelector: '[data-tour="clients-create"]',
    createFieldAnchors: [
      { selector: '[data-tour="clients-field-name"]', titleKey: 'fieldNameTitle', descKey: 'fieldNameDesc' },
      { selector: '[data-tour="clients-field-phone"]', titleKey: 'fieldPhoneTitle', descKey: 'fieldPhoneDesc' },
      { selector: '[data-tour="clients-field-email"]', titleKey: 'fieldEmailTitle', descKey: 'fieldEmailDesc' },
      { selector: '[data-tour="clients-field-birthDate"]', titleKey: 'fieldBirthTitle', descKey: 'fieldBirthDesc' },
      { selector: '[data-tour="clients-field-city"]', titleKey: 'fieldCityTitle', descKey: 'fieldCityDesc' },
      { selector: '[data-tour="clients-field-district"]', titleKey: 'fieldDistrictTitle', descKey: 'fieldDistrictDesc' },
      { selector: '[data-tour="clients-field-address"]', titleKey: 'fieldAddressTitle', descKey: 'fieldAddressDesc' },
      { selector: '[data-tour="clients-field-clientType"]', titleKey: 'fieldTypeTitle', descKey: 'fieldTypeDesc' },
      { selector: '[data-tour="clients-field-whatsapp"]', titleKey: 'fieldWhatsappTitle', descKey: 'fieldWhatsappDesc' },
    ],
    i18n: 'clients',
  },
  {
    id: 'services_v1',
    ensurePath: '/services',
    eligibility: {
      requiresFeatures: ['services'],
      requiresPermissions: ['canViewServices'],
    },
    navSelector: '[data-tour="nav-services"]',
    titleSelector: '[data-tour="services-title"]',
    contentSelector: '[data-tour="services-list"]',
    filterAnchors: [
      {
        selector: '[data-tour="services-filter-search"]',
        titleKey: 'filterSearchTitle',
        descKey: 'filterSearchDesc',
      },
      {
        selector: '[data-tour="services-filter-category"]',
        titleKey: 'filterCategoryTitle',
        descKey: 'filterCategoryDesc',
      },
    ],
    createOpenSelector: '[data-tour="services-create"]',
    createFieldAnchors: [
      { selector: '[data-tour="services-field-name"]', titleKey: 'fieldNameTitle', descKey: 'fieldNameDesc' },
      { selector: '[data-tour="services-field-category"]', titleKey: 'fieldCategoryTitle', descKey: 'fieldCategoryDesc' },
      { selector: '[data-tour="services-field-pricing"]', titleKey: 'fieldPricingTitle', descKey: 'fieldPricingDesc' },
      { selector: '[data-tour="services-field-price"]', titleKey: 'fieldPriceTitle', descKey: 'fieldPriceDesc' },
      { selector: '[data-tour="services-field-stock"]', titleKey: 'fieldStockTitle', descKey: 'fieldStockDesc' },
      { selector: '[data-tour="services-field-description"]', titleKey: 'fieldDescTitle', descKey: 'fieldDescDesc' },
    ],
    i18n: 'services',
  },
  {
    id: 'appointments_v1',
    ensurePath: '/appointments',
    eligibility: {
      requiresFeatures: ['appointments'],
      requiresAnyPermissions: ['canViewAppointments', 'canViewAppointmentRequests'],
    },
    navParentSelector: '[data-tour="nav-appointments"]',
    navSelector: '[data-tour="nav-appointments-calendar"]',
    titleSelector: '[data-tour="appointments-title"]',
    actionsSelector: '[data-tour="appointments-actions"]',
    contentSelector: '[data-tour="appointments-content"]',
    i18n: 'appointments',
  },
  {
    id: 'referents_v1',
    ensurePath: '/referents',
    eligibility: {
      requiresFeatures: ['referents'],
      requiresPermissions: ['canViewReferents'],
    },
    navSelector: '[data-tour="nav-referents"]',
    titleSelector: '[data-tour="referents-title"]',
    actionsSelector: '[data-tour="referents-actions"]',
    contentSelector: '[data-tour="referents-content"]',
    i18n: 'referents',
  },
  {
    id: 'marketing_v1',
    ensurePath: '/marketing',
    eligibility: {
      requiresFeatures: ['marketing'],
      requiresPermissions: ['canViewMarketing'],
    },
    navSelector: '[data-tour="nav-marketing"]',
    titleSelector: '[data-tour="marketing-title"]',
    actionsSelector: '[data-tour="marketing-actions"]',
    contentSelector: '[data-tour="marketing-content"]',
    i18n: 'marketing',
  },
  {
    id: 'messages_clients_v1',
    ensurePath: '/messages-clients',
    eligibility: {
      requiresFeatures: ['messagesClients'],
      requiresPermissions: ['canViewMessagesClients'],
    },
    navSelector: '[data-tour="nav-messages-clients"]',
    titleSelector: '[data-tour="messages-clients-title"]',
    actionsSelector: '[data-tour="messages-clients-actions"]',
    contentSelector: '[data-tour="messages-clients-content"]',
    i18n: 'messagesClients',
  },
  {
    id: 'inventory_products_v1',
    ensurePath: '/inventory/products',
    eligibility: {
      requiresFeatures: ['inventory'],
      requiresPermissions: ['canViewProducts'],
    },
    navParentSelector: '[data-tour="nav-inventory"]',
    navSelector: '[data-tour="nav-inventory-products"]',
    titleSelector: '[data-tour="inventory-products-title"]',
    actionsSelector: '[data-tour="inventory-products-actions"]',
    contentSelector: '[data-tour="inventory-products-content"]',
    filterAnchors: [
      {
        selector: '[data-tour="inventory-products-filter-search"]',
        titleKey: 'filterSearchTitle',
        descKey: 'filterSearchDesc',
      },
      {
        selector: '[data-tour="inventory-products-filter-category"]',
        titleKey: 'filterCategoryTitle',
        descKey: 'filterCategoryDesc',
      },
      {
        selector: '[data-tour="inventory-products-filter-stock"]',
        titleKey: 'filterStockTitle',
        descKey: 'filterStockDesc',
      },
    ],
    createOpenSelector: '[data-tour="inventory-products-create"]',
    createFieldAnchors: [
      { selector: '[data-tour="products-field-name"]', titleKey: 'fieldNameTitle', descKey: 'fieldNameDesc' },
      { selector: '[data-tour="products-field-category"]', titleKey: 'fieldCategoryTitle', descKey: 'fieldCategoryDesc' },
      { selector: '[data-tour="products-field-barcode"]', titleKey: 'fieldBarcodeTitle', descKey: 'fieldBarcodeDesc' },
      { selector: '[data-tour="products-field-cost"]', titleKey: 'fieldCostTitle', descKey: 'fieldCostDesc' },
      { selector: '[data-tour="products-field-price"]', titleKey: 'fieldPriceTitle', descKey: 'fieldPriceDesc' },
      { selector: '[data-tour="products-field-stock"]', titleKey: 'fieldStockTitle', descKey: 'fieldStockDesc' },
      { selector: '[data-tour="products-field-minStock"]', titleKey: 'fieldMinStockTitle', descKey: 'fieldMinStockDesc' },
    ],
    i18n: 'inventory',
  },
  {
    id: 'inventory_movements_v1',
    ensurePath: '/inventory/movements',
    eligibility: {
      requiresFeatures: ['inventory'],
      requiresPermissions: ['canViewStockMovements'],
    },
    navParentSelector: '[data-tour="nav-inventory"]',
    navSelector: '[data-tour="nav-inventory-movements"]',
    titleSelector: '[data-tour="inventory-movements-title"]',
    actionsSelector: '[data-tour="inventory-movements-actions"]',
    contentSelector: '[data-tour="inventory-movements-content"]',
    filterAnchors: [
      {
        selector: '[data-tour="inventory-movements-filter-period"]',
        titleKey: 'filterPeriodTitle',
        descKey: 'filterPeriodDesc',
      },
      {
        selector: '[data-tour="inventory-movements-filter-type"]',
        titleKey: 'filterTypeTitle',
        descKey: 'filterTypeDesc',
      },
      {
        selector: '[data-tour="inventory-movements-filter-search"]',
        titleKey: 'filterSearchTitle',
        descKey: 'filterSearchDesc',
      },
      {
        selector: '[data-tour="inventory-movements-filter-site"]',
        titleKey: 'filterSiteTitle',
        descKey: 'filterSiteDesc',
      },
    ],
    i18n: 'inventoryMovements',
  },
  {
    id: 'inventory_losses_v1',
    ensurePath: '/inventory/losses',
    eligibility: {
      requiresFeatures: ['inventoryLosses'],
      requiresPermissions: ['canViewInventoryLosses'],
    },
    navParentSelector: '[data-tour="nav-inventory"]',
    navSelector: '[data-tour="nav-inventory-losses"]',
    titleSelector: '[data-tour="inventory-losses-title"]',
    actionsSelector: '[data-tour="inventory-losses-actions"]',
    contentSelector: '[data-tour="inventory-losses-content"]',
    i18n: 'inventoryLosses',
  },
  {
    id: 'inventory_audit_v1',
    ensurePath: '/inventory/audit',
    eligibility: {
      requiresFeatures: ['inventory'],
      requiresPermissions: ['canViewInventoryAudit'],
    },
    navParentSelector: '[data-tour="nav-inventory"]',
    navSelector: '[data-tour="nav-inventory-audit"]',
    titleSelector: '[data-tour="inventory-audit-title"]',
    actionsSelector: '[data-tour="inventory-audit-actions"]',
    contentSelector: '[data-tour="inventory-audit-content"]',
    i18n: 'inventoryAudit',
  },
  {
    id: 'inventory_external_v1',
    ensurePath: '/inventory/external-dispatches',
    eligibility: {
      requiresFeatures: ['inventoryExternalDispatch'],
      requiresPermissions: ['canViewExternalDispatches'],
    },
    navParentSelector: '[data-tour="nav-inventory"]',
    navSelector: '[data-tour="nav-inventory-external"]',
    titleSelector: '[data-tour="inventory-external-title"]',
    actionsSelector: '[data-tour="inventory-external-actions"]',
    contentSelector: '[data-tour="inventory-external-content"]',
    i18n: 'inventoryExternal',
  },
  {
    id: 'inventory_reports_v1',
    ensurePath: '/inventory/dashboard',
    eligibility: {
      requiresFeatures: ['inventory'],
      requiresPermissions: ['canViewInventoryReports'],
    },
    navParentSelector: '[data-tour="nav-inventory"]',
    navSelector: '[data-tour="nav-inventory-reports"]',
    titleSelector: '[data-tour="inventory-reports-title"]',
    contentSelector: '[data-tour="inventory-reports-content"]',
    i18n: 'inventoryReports',
  },
  {
    id: 'suppliers_v1',
    ensurePath: '/suppliers',
    eligibility: {
      requiresFeatures: ['purchasesExpenses'],
      requiresPermissions: ['canViewSuppliers'],
    },
    navParentSelector: '[data-tour="nav-purchases"]',
    navSelector: '[data-tour="nav-suppliers"]',
    titleSelector: '[data-tour="suppliers-title"]',
    actionsSelector: '[data-tour="suppliers-actions"]',
    contentSelector: '[data-tour="suppliers-content"]',
    i18n: 'suppliers',
  },
  {
    id: 'deliveries_v1',
    ensurePath: '/suppliers/deliveries',
    eligibility: {
      requiresFeatures: ['purchasesExpenses'],
      requiresPermissions: ['canViewDeliveries'],
    },
    navParentSelector: '[data-tour="nav-purchases"]',
    navSelector: '[data-tour="nav-deliveries"]',
    titleSelector: '[data-tour="deliveries-title"]',
    actionsSelector: '[data-tour="deliveries-actions"]',
    contentSelector: '[data-tour="deliveries-content"]',
    i18n: 'deliveries',
  },
  {
    id: 'expenses_v1',
    ensurePath: '/expenses',
    eligibility: {
      requiresFeatures: ['purchasesExpenses'],
      requiresPermissions: ['canViewExpenses'],
    },
    navParentSelector: '[data-tour="nav-purchases"]',
    navSelector: '[data-tour="nav-expenses"]',
    titleSelector: '[data-tour="expenses-title"]',
    actionsSelector: '[data-tour="expenses-actions"]',
    contentSelector: '[data-tour="expenses-content"]',
    i18n: 'expenses',
  },
  {
    id: 'expense_notes_v1',
    ensurePath: '/expense-notes',
    eligibility: {
      requiresFeatures: ['expenseNotes'],
      requiresPermissions: ['canViewExpenseNotes'],
    },
    navParentSelector: '[data-tour="nav-purchases"]',
    navSelector: '[data-tour="nav-expense-notes"]',
    titleSelector: '[data-tour="expense-notes-title"]',
    actionsSelector: '[data-tour="expense-notes-actions"]',
    contentSelector: '[data-tour="expense-notes-content"]',
    i18n: 'expenseNotes',
  },
  {
    id: 'accounting_v1',
    ensurePath: '/accounting/dashboard',
    eligibility: {
      requiresFeatures: ['accounting'],
      requiresPermissions: ['canViewAccounting'],
    },
    navParentSelector: '[data-tour="nav-accounting"]',
    navSelector: '[data-tour="nav-accounting-dashboard"]',
    titleSelector: '[data-tour="accounting-title"]',
    contentSelector: '[data-tour="accounting-content"]',
    i18n: 'accounting',
  },
  {
    id: 'employees_v1',
    ensurePath: '/employees',
    eligibility: {
      requiresFeatures: ['employeesPayroll'],
      requiresPermissions: ['canViewEmployees'],
    },
    navParentSelector: '[data-tour="nav-hr"]',
    navSelector: '[data-tour="nav-employees"]',
    titleSelector: '[data-tour="employees-title"]',
    actionsSelector: '[data-tour="employees-actions"]',
    contentSelector: '[data-tour="employees-content"]',
    filterAnchors: [
      {
        selector: '[data-tour="employees-filter-search"]',
        titleKey: 'filterSearchTitle',
        descKey: 'filterSearchDesc',
      },
      {
        selector: '[data-tour="employees-filter-status"]',
        titleKey: 'filterStatusTitle',
        descKey: 'filterStatusDesc',
      },
    ],
    createOpenSelector: '[data-tour="employees-create"]',
    createFieldAnchors: [
      { selector: '[data-tour="employees-field-name"]', titleKey: 'fieldNameTitle', descKey: 'fieldNameDesc' },
      { selector: '[data-tour="employees-field-role"]', titleKey: 'fieldRoleTitle', descKey: 'fieldRoleDesc' },
      { selector: '[data-tour="employees-field-email"]', titleKey: 'fieldEmailTitle', descKey: 'fieldEmailDesc' },
      { selector: '[data-tour="employees-field-phone"]', titleKey: 'fieldPhoneTitle', descKey: 'fieldPhoneDesc' },
      { selector: '[data-tour="employees-field-salary"]', titleKey: 'fieldSalaryTitle', descKey: 'fieldSalaryDesc' },
      { selector: '[data-tour="employees-field-sites"]', titleKey: 'fieldSitesTitle', descKey: 'fieldSitesDesc' },
      { selector: '[data-tour="employees-field-active"]', titleKey: 'fieldActiveTitle', descKey: 'fieldActiveDesc' },
    ],
    i18n: 'employees',
  },
  {
    id: 'performance_v1',
    ensurePath: '/performance-indicators',
    eligibility: {
      requiresFeatures: ['performanceIndicators'],
      requiresPermissions: ['canViewPerformanceObjectives'],
    },
    navParentSelector: '[data-tour="nav-hr"]',
    navSelector: '[data-tour="nav-performance"]',
    titleSelector: '[data-tour="performance-title"]',
    actionsSelector: '[data-tour="performance-actions"]',
    contentSelector: '[data-tour="performance-content"]',
    i18n: 'performance',
  },
  {
    id: 'opportunities_v1',
    ensurePath: '/activity/opportunities',
    eligibility: {
      requiresFeatures: ['opportunities'],
      requiresPermissions: ['canViewSalesPipeline'],
    },
    navParentSelector: '[data-tour="nav-activity"]',
    navSelector: '[data-tour="nav-opportunities"]',
    titleSelector: '[data-tour="opportunities-title"]',
    actionsSelector: '[data-tour="opportunities-actions"]',
    contentSelector: '[data-tour="opportunities-content"]',
    i18n: 'opportunities',
  },
];

export const dashboardTourDefinition: ProductTourDefinition = {
  id: 'dashboard_v1',
  ensurePath: '/dashboard',
  eligibility: {
    requiresFeatures: ['dashboard'],
    requiresPermissions: ['canViewDashboard'],
  },
  requiredSelectors: ['[data-tour="dashboard-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell, business, permissions }) => {
    const b = business ?? null;
    const hasPos = isFeatureEnabled(b, 'pos') && !!permissions?.canViewPrestations;
    const hasInv = isFeatureEnabled(b, 'inventory') && !!permissions?.canViewProductSales;
    const hasMkt = isFeatureEnabled(b, 'marketing') && !!permissions?.canViewMarketing;
    const hasAcc = isFeatureEnabled(b, 'accounting');
    return compactSteps([
      {
        element: '[data-tour="nav-dashboard"]',
        popover: {
          title: t('tours.dashboard.stepNavTitle'),
          description: t('tours.dashboard.stepNavDesc'),
          side: 'right',
          align: 'start',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen('/dashboard', pathname, navigate, drv, layoutShell),
        },
      },
      step('[data-tour="dashboard-title"]', t('tours.dashboard.stepTitle'), t('tours.dashboard.stepTitleDesc')),
      step(
        '[data-tour="dashboard-filters"]',
        t('tours.dashboard.stepFiltersTitle'),
        t('tours.dashboard.stepFiltersDesc')
      ),
      step('[data-tour="dashboard-kpis"]', t('tours.dashboard.stepKpisTitle'), t('tours.dashboard.stepKpisDesc')),
      hasPos
        ? step(
            '[data-tour="dashboard-block-prestations"]',
            t('tours.dashboard.stepPrestationsTitle'),
            t('tours.dashboard.stepPrestationsDesc')
          )
        : null,
      hasInv
        ? step(
            '[data-tour="dashboard-block-inventory"]',
            t('tours.dashboard.stepInventoryTitle'),
            t('tours.dashboard.stepInventoryDesc')
          )
        : null,
      hasMkt
        ? step(
            '[data-tour="dashboard-block-marketing"]',
            t('tours.dashboard.stepMarketingTitle'),
            t('tours.dashboard.stepMarketingDesc')
          )
        : null,
      hasAcc
        ? step(
            '[data-tour="dashboard-block-accounting"]',
            t('tours.dashboard.stepAccountingTitle'),
            t('tours.dashboard.stepAccountingDesc')
          )
        : null,
      step(
        '[data-tour="dashboard-content"]',
        t('tours.dashboard.stepContent'),
        t('tours.dashboard.stepContentDesc'),
        'top'
      ),
    ]);
  },
};

/** Ramassage : inbox puis page Configuration (lien, zones, forfait, vitrine). */
export const pickupTourDefinition: ProductTourDefinition = {
  id: 'pickup_v1',
  ensurePath: '/pickups',
  eligibility: {
    requiresFeatures: ['pickup'],
    requiresPermissions: ['canViewPickups'],
  },
  requiredSelectors: ['[data-tour="pickups-title"]'],
  buildSteps: ({ t, navigate, pathname, layoutShell, permissions }) => {
    const p = 'tours.pickup';
    const canManage = !!permissions?.canManagePickups;
    return compactSteps([
      ...buildNavHighlightSteps({
        leafSelector: '[data-tour="nav-pickups"]',
        leafTitle: t(`${p}.stepNavTitle`),
        leafDesc: t(`${p}.stepNavDesc`),
        parentTitle: t(`${p}.stepNavTitle`),
        parentDesc: t(`${p}.stepNavDesc`),
        ensurePath: '/pickups',
        pathname,
        navigate,
        layoutShell,
        side: 'right',
      }),
      step('[data-tour="pickups-title"]', t(`${p}.stepTitle`), t(`${p}.stepTitleDesc`)),
      step(
        '[data-tour="pickups-content"]',
        t(`${p}.stepContent`),
        t(`${p}.stepContentDesc`),
        'top'
      ),
      canManage
        ? {
            element: '[data-tour="pickups-actions"]',
            popover: {
              title: t(`${p}.stepActions`),
              description: t(`${p}.stepActionsDesc`),
              side: 'bottom',
              align: 'start',
              onNextClick: (_e, _s, { driver: drv }) =>
                navThen('/pickups/settings', pathname, navigate, drv, layoutShell),
            },
          }
        : null,
      canManage
        ? step(
            '[data-tour="pickup-settings-title"]',
            t(`${p}.stepSettingsTitle`),
            t(`${p}.stepSettingsTitleDesc`)
          )
        : null,
      canManage
        ? step(
            '[data-tour="pickup-settings-link"]',
            t(`${p}.stepSettingsLinkTitle`),
            t(`${p}.stepSettingsLinkDesc`),
            'top'
          )
        : null,
      canManage
        ? step(
            '[data-tour="pickup-settings-publish"]',
            t(`${p}.stepSettingsPublishTitle`),
            t(`${p}.stepSettingsPublishDesc`),
            'top'
          )
        : null,
      canManage
        ? step(
            '[data-tour="pickup-settings-zones"]',
            t(`${p}.stepSettingsZonesTitle`),
            t(`${p}.stepSettingsZonesDesc`),
            'top'
          )
        : null,
      canManage
        ? step(
            '[data-tour="pickup-settings-fee"]',
            t(`${p}.stepSettingsFeeTitle`),
            t(`${p}.stepSettingsFeeDesc`),
            'top'
          )
        : null,
      canManage
        ? step(
            '[data-tour="pickup-settings-identity"]',
            t(`${p}.stepSettingsIdentityTitle`),
            t(`${p}.stepSettingsIdentityDesc`),
            'top'
          )
        : null,
      canManage
        ? step(
            '[data-tour="pickup-settings-media"]',
            t(`${p}.stepSettingsMediaTitle`),
            t(`${p}.stepSettingsMediaDesc`),
            'top'
          )
        : null,
      canManage
        ? step(
            '[data-tour="pickup-settings-description"]',
            t(`${p}.stepSettingsDescriptionTitle`),
            t(`${p}.stepSettingsDescriptionDesc`),
            'top'
          )
        : null,
      canManage
        ? step(
            '[data-tour="pickup-settings-contacts"]',
            t(`${p}.stepSettingsContactsTitle`),
            t(`${p}.stepSettingsContactsDesc`),
            'top'
          )
        : null,
    ]);
  },
};

export const ALL_TOUR_DEFINITIONS: ProductTourDefinition[] = [
  helpTourDefinition,
  helpGuidesTourDefinition,
  helpFaqTourDefinition,
  helpTicketsTourDefinition,
  refreshAppTourDefinition,
  pushNotificationsTourDefinition,
  posSaleTourDefinition,
  posInstallmentTourDefinition,
  payrollsTourDefinition,
  activityReportsTourDefinition,
  serviceCategoriesTourDefinition,
  posReportsTourDefinition,
  posSaleCorrectionTourDefinition,
  catalogPacksTourDefinition,
  manufacturingTourDefinition,
  dashboardTourDefinition,
  pickupTourDefinition,
  ...MODULE_TOURS.map(buildModuleTour),
];
